/**
 * NotificationPrefs — opt-in panel embedded in /espace/profil.
 *
 * Displays 4 category toggles (events, news, festivities 2027, direct messages)
 * so members can fine-tune what pushes they receive. Also reports whether their
 * account has any registered device — if not, we show a gentle hint to install
 * the PRESENCE app.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@/i18n/I18nContext';
import api from '@/lib/api';

const CATEGORIES = [
  {
    key: 'events',
    labels: {
      fr: { title: 'Rappels d\'événements', desc: 'Offices, concerts, conférences à venir au Prieuré.' },
      en: { title: 'Event reminders', desc: 'Upcoming offices, concerts and conferences at the Priory.' },
    },
  },
  {
    key: 'news',
    labels: {
      fr: { title: 'Nouvelles annonces', desc: 'Articles, communiqués et lettres de l\'Association.' },
      en: { title: 'News & announcements', desc: 'New articles, statements and letters from the Association.' },
    },
  },
  {
    key: 'festivities_2027',
    labels: {
      fr: { title: 'Fêtes de saint Léonard 2027', desc: 'Invitations, ouverture des inscriptions, planning et remerciements.' },
      en: { title: 'Saint Léonard Feasts 2027', desc: 'Invitations, registration openings, schedule and thank-yous.' },
    },
  },
  {
    key: 'member_direct',
    labels: {
      fr: { title: 'Messages directs', desc: 'Invitations bénévolat, réponses forum, rappels de covoiturage.' },
      en: { title: 'Direct messages', desc: 'Volunteer invitations, forum replies, carpool reminders.' },
    },
  },
];

export default function NotificationPrefs() {
  const { locale } = useI18n();
  const l = locale === 'fr' ? 'fr' : 'en';
  const [prefs, setPrefs] = useState(null);
  const [deviceCount, setDeviceCount] = useState(0);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    api
      .get('/espace/notification-preferences')
      .then((r) => {
        if (cancelled) return;
        setPrefs(r.data.preferences);
        setDeviceCount(r.data.device_count || 0);
      })
      .catch(() => {
        if (!cancelled) setPrefs(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (prefs === null)
    return (
      <p className="font-body italic text-sm" style={{ color: 'var(--muted)' }}>
        {l === 'fr' ? 'Chargement des préférences…' : 'Loading preferences…'}
      </p>
    );
  if (prefs === false)
    return (
      <p className="font-body text-sm" style={{ color: 'var(--cinnabar)' }}>
        {l === 'fr'
          ? 'Impossible de charger les préférences de notification.'
          : 'Unable to load notification preferences.'}
      </p>
    );

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next); // optimistic update
    setSaving(key);
    setMsg('');
    try {
      const r = await api.patch('/espace/notification-preferences', {
        [key]: next[key],
      });
      setPrefs(r.data.preferences);
      setMsg(l === 'fr' ? '✓ Enregistré' : '✓ Saved');
    } catch (e) {
      setPrefs(prefs); // rollback
      setMsg(l === 'fr' ? 'Échec — réessayez.' : 'Failed — try again.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div
      data-testid="notif-prefs"
      className="bg-white border p-6 mt-6"
      style={{ borderColor: 'var(--border)' }}
    >
      <h2
        className="font-heading text-2xl mb-1"
        style={{ color: 'var(--ink)' }}
      >
        {l === 'fr' ? 'Notifications' : 'Notifications'}
      </h2>
      <p className="font-body text-sm mb-6" style={{ color: 'var(--muted)' }}>
        {l === 'fr'
          ? 'Choisissez ce que vous souhaitez recevoir sur l\'application PRESENCE.'
          : 'Choose what you want to receive through the PRESENCE app.'}
      </p>

      {deviceCount === 0 && (
        <div
          className="border-l-4 p-3 mb-5 text-sm font-body"
          style={{ borderColor: 'var(--gold)', background: 'var(--parchment-2)' }}
          data-testid="notif-prefs-no-device"
        >
          {l === 'fr'
            ? "Aucun appareil enregistré pour l'instant. Installez l'application PRESENCE (iOS/Android) et connectez-vous depuis celle-ci pour activer les notifications."
            : 'No device registered yet. Install the PRESENCE app (iOS/Android) and sign in from it to enable notifications.'}
        </div>
      )}

      <ul className="space-y-3">
        {CATEGORIES.map((c) => {
          const lbl = c.labels[l] || c.labels.fr;
          const checked = Boolean(prefs[c.key]);
          const busy = saving === c.key;
          return (
            <li
              key={c.key}
              className="flex items-start justify-between gap-4 py-3 border-b last:border-b-0"
              style={{ borderColor: 'var(--border)' }}
            >
              <div>
                <div
                  className="font-body font-semibold"
                  style={{ color: 'var(--ink)' }}
                >
                  {lbl.title}
                </div>
                <div
                  className="font-body text-xs mt-1"
                  style={{ color: 'var(--muted)' }}
                >
                  {lbl.desc}
                </div>
              </div>
              <button
                type="button"
                data-testid={`notif-toggle-${c.key}`}
                aria-checked={checked}
                role="switch"
                onClick={() => toggle(c.key)}
                disabled={busy}
                className="relative inline-flex items-center flex-shrink-0 mt-1 transition-colors"
                style={{
                  width: 46,
                  height: 26,
                  borderRadius: 13,
                  background: checked ? 'var(--cinnabar, #7b1f2b)' : '#d8cfbf',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: 'inline-block',
                    width: 20,
                    height: 20,
                    background: '#fff',
                    borderRadius: 10,
                    transform: `translateX(${checked ? 22 : 4}px)`,
                    transition: 'transform 180ms ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,.2)',
                  }}
                />
              </button>
            </li>
          );
        })}
      </ul>

      {msg && (
        <p
          className="font-body text-xs mt-4"
          style={{ color: msg.startsWith('✓') ? 'var(--moss, #4a6b3a)' : 'var(--cinnabar)' }}
          data-testid="notif-prefs-msg"
        >
          {msg}
        </p>
      )}
    </div>
  );
}
